import { parsePhoneNumber } from 'react-phone-number-input';
import PropTypes from "prop-types";
import PhoneNumberInput from 'react-phone-number-input/input';
import { useState, useEffect, useCallback, startTransition, forwardRef } from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';


import { Iconify } from '../iconify/iconify';
import { CountryListPopover } from './list-popover';
import { useDispatch, useSelector } from "react-redux";
import { fetchCountries } from "./phone-input-slice";

// ----------------------------------------------------------------------

export function PhoneInput({
  sx,
  size,
  value,
  label,
  onChange,
  placeholder,
  required=true,
  disableSelect,
  variant = 'outlined',
  country: inputCountryCode,
  ...other
}) {
  const defaultCountryCode = getCountryCode(value, inputCountryCode);
  const dispatch = useDispatch();
  const { countryList } = useSelector((state) => state?.countries);
  useEffect(() => {
    dispatch(fetchCountries());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const [searchCountry, setSearchCountry] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(defaultCountryCode);

  const countryOptions = countryList?.map((country) => ({
  code: country?.isoCode,
  label: country?.countryName,
  phone: country?.countryCode,
}));
  const hasLabel = !!label;

  const cleanValue = value?.replace(/[\s-]+/g, '');
  const showDialCode = other?.showDialCode;
  const dialCode = countryList?.find((c) => c?.isoCode === selectedCountry)?.countryCode || '';

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCountry(defaultCountryCode);
    }
  }, [defaultCountryCode, selectedCountry]);

  const handleClickCountry = (inputValue) => {
    startTransition(() => {
      setSelectedCountry(inputValue);
    });
  };

  const handleSearchCountry = (inputValue) => {
    setSearchCountry(inputValue);
  };
  const dialCodeText = showDialCode ? getDialCodeString(cleanValue, selectedCountry, countryOptions) : null;
const handleChange = (val) => {
  const cleaned = val?.replace(/[\s-]+/g, '') || '';
  onChange(cleaned);
};
  return (
  
      <Box
        sx={[
          {
            borderRadius: "8px",
            "--popover-button-mr": "12px",
            "--popover-button-height": "22px",
            "--popover-button-width": variant === "standard" ? "48px" : "60px", position: "relative",
            ...(!disableSelect && {
              [`& .${inputBaseClasses.input}`]: {
                pl: "calc(var(--popover-button-width) + var(--popover-button-mr))",
              },
            }),
          },
          ...(showDialCode && !disableSelect? [{
                  [`& .${inputBaseClasses.input}`]: {
                    pl: "calc(var(--popover-button-width) + var(--popover-button-mr) + 50px)",},
                },
              ]: []),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {!disableSelect && (
          <CountryListPopover
            countries={countryOptions}
            searchCountry={searchCountry}
            countryCode={selectedCountry}
            onClickCountry={handleClickCountry}
            onSearchCountry={handleSearchCountry}
            sx={{
              pl: variant === "standard" ? 0 : 1.5,
              ...(variant === "standard" && hasLabel && { mt: size === "small" ? "16px" : "20px" }),
              ...((variant === "filled" || variant === "outlined") && {
                mt: size === "small" ? "8px" : "10px",}),
              ...(variant === "filled" && hasLabel && { mt: size === "small" ? "21px" : "25px" }),
            }}
            dialCodeText={dialCodeText}
          />
        )}

        <PhoneNumberInput
          size={size}
          value={cleanValue}
          variant={variant}
          onChange={handleChange}
          hiddenLabel={!label}
          country={selectedCountry}
          inputComponent={CustomInput}
          disableSelect={disableSelect}
          placeholder={placeholder ?? "Contact Number"}
          label={
            label ? (
              <Box component="span"
                sx={{ display: "flex", alignItems: "center", gap: "2px" }}
              >
                {label}
                {required && (
                  <Box component="span" sx={{ color: "error.main" }}>
                    *
                  </Box>
                )}
              </Box>
            ) : null
          }
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            selectedCountry,
            autoComplete: 'off',
            sx: {
              borderRadius: "8px",
              // add padding so text/placeholder starts after dial code
              [`& .${inputBaseClasses.input}`]: {
                p: "12px", // adjust based on dial code width
                fontSize: "14px",
                ml: 2,
              },
            },
            startAdornment: dialCode ? (
              <InputAdornment
                position="start"
                sx={{
                  alignItems: "center",
                  "& span": {
                    display: "inline-block",
                    minWidth: "28px",
                    textAlign: "center",
                    fontSize: "14px",
                    ml: 2,
                  },
                }}
              >
                {/* <Box
                  component="span"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  {dialCodeText}
                </Box> */}
              </InputAdornment>
            ) : undefined,
            endAdornment: cleanValue && (
              <InputAdornment position="end">
                <IconButton size="small" edge="end" onClick={handleClear}>
                  <Iconify width={14} icon="mingcute:close-line" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          {...other}
        />
      </Box>
  );
}

// ----------------------------------------------------------------------

const CustomInput = forwardRef(function CustomInput(
  { label, variant = "outlined", startAdornment, endAdornment, ...props },
  ref
) {
  let val = props?.value?.replace(/[\s-]+/g, '') || '';
  const country = props?.InputProps?.selectedCountry || 'IN';

  if (country === 'IN' && val?.length > 10 && val?.startsWith('0')) {
    val = val?.slice(1);
  }

  return (
    <TextField
      variant={variant}
      inputRef={ref}
      InputProps={{
        startAdornment,
        endAdornment,
      }}
      {...props}
      value={val}
    />
  );
});

CustomInput.propTypes = {
  label: PropTypes.any,
  variant: PropTypes.string,
  startAdornment: PropTypes.any,
  endAdornment: PropTypes.any,
  value: PropTypes.any,
  InputProps: PropTypes.object,
};

// ----------------------------------------------------------------------

function getCountryCode(inputValue, countryCode) {
  if (inputValue) {
    const phoneNumber = parsePhoneNumber(inputValue);
    return phoneNumber?.country;
  }

  return countryCode;
}
function getDialCodeString(inputValue, country, countryList) {
  try {
    const phoneNumber = inputValue ? parsePhoneNumber(inputValue) : undefined;
    if (phoneNumber?.countryCallingCode) {
      return `+${phoneNumber.countryCallingCode}`;
    }
  } catch {
    // ignore
  }
  if (country) {
    const found = countryList?.find((c) => c?.code === country);
    if (found?.phone) {
      return `+${found.phone}`;
    }
  }
  return null;
}


PhoneInput.propTypes = {
  InputProps: PropTypes.object,
  country: PropTypes.any,
  disableSelect: PropTypes.any,
  endAdornment: PropTypes.any,
  label: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.any,
  required: PropTypes.any,
  size: PropTypes.any,
  startAdornment: PropTypes.any,
  sx: PropTypes.object,
  value: PropTypes.any,
  variant: PropTypes.any,
};
